import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import {
  BusinessOutlined,
  EmojiEventsOutlined,
  HistoryOutlined,
  LocationOnOutlined,
  RedeemOutlined,
  SportsEsportsOutlined,
  VideogameAssetOutlined,
} from "@mui/icons-material";

import { useEffect, useMemo, useState } from "react";
import { useGetIdentity, useNotify } from "react-admin";

import { appConfig } from "@/commons";

// ============================================================
// TYPES
// ============================================================

type StatsOverview = {
  users_id?: string;
  play_count?: number;
  game_count?: number;
  last_play?: string | null;
  entity_count?: number;
  location_count?: number;
  max_row_score?: number;
};

type VoucherOverview = {
  claimed?: number;
  consumed?: number;

  // Allows compatibility if backend uses other naming.
  claimed_count?: number;
  consumed_count?: number;

  total_claimed?: number;
  total_consumed?: number;

  [key: string]: unknown;
};

type RankingItem = {
  id?: string;
  user_id?: string;
  users_id?: string;

  pseudo?: string;
  username?: string;
  userName?: string;
  full_name?: string;

  score?: number;
  points?: number;
  max_score?: number;

  rank?: number;

  [key: string]: unknown;
};

type DashboardData = {
  overview: StatsOverview | null;
  voucherOverview: VoucherOverview | null;
  rankingDay: RankingItem[];
  rankingWeek: RankingItem[];
};

// ============================================================
// HELPERS
// ============================================================

const numberValue = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const fetchJson = async <T,>(path: string): Promise<T> => {
  const response = await fetch(`${appConfig.api.baseUrl}${path}`, {
    method: "GET",

    credentials: "include",

    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);

    const message = Array.isArray(data?.message)
      ? data.message.join(", ")
      : data?.message;

    throw new Error(message || `Erreur HTTP ${response.status}`);
  }

  return response.json();
};

const getClaimedCount = (data: VoucherOverview | null) =>
  numberValue(data?.claimed ?? data?.claimed_count ?? data?.total_claimed);

const getConsumedCount = (data: VoucherOverview | null) =>
  numberValue(data?.consumed ?? data?.consumed_count ?? data?.total_consumed);

const getRankingName = (item: RankingItem) =>
  item.pseudo ?? item.username ?? item.userName ?? item.full_name ?? "Joueur";

const getRankingScore = (item: RankingItem) =>
  numberValue(item.score ?? item.points ?? item.max_score);

const getRankingUserId = (item: RankingItem) =>
  item.user_id ?? item.users_id ?? item.id;

const formatNumber = (value: number) =>
  new Intl.NumberFormat("fr-FR").format(value);

const formatLastPlay = (value?: string | null) => {
  if (!value) {
    return "Aucune partie jouée";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date indisponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const getGreeting = () => {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Bonjour";
  }

  if (hour < 18) {
    return "Bon après-midi";
  }

  return "Bonsoir";
};

// ============================================================
// SMALL COMPONENTS
// ============================================================

type StatCardProps = {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  helper?: string;
};

const StatCard = ({ label, value, icon, helper }: StatCardProps) => {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 3,
        borderColor: "divider",
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(108, 0, 136, 0.08)",
              color: "#6C0088",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>

            <Typography variant="h5" fontWeight={700} color="#6C0088">
              {value}
            </Typography>

            {helper && (
              <Typography variant="caption" color="text.secondary">
                {helper}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ============================================================
// DASHBOARD
// ============================================================

export default function Dashboard() {
  const { identity } = useGetIdentity();

  const notify = useNotify();

  const [loading, setLoading] = useState(true);

  const [rankingTab, setRankingTab] = useState<"day" | "week">("day");

  const [data, setData] = useState<DashboardData>({
    overview: null,
    voucherOverview: null,
    rankingDay: [],
    rankingWeek: [],
  });

  // ============================================================
  // LOAD
  // ============================================================

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      setLoading(true);

      const [
        overviewResult,
        voucherResult,
        rankingDayResult,
        rankingWeekResult,
      ] = await Promise.allSettled([
        fetchJson<StatsOverview>("/stats/overview"),

        fetchJson<VoucherOverview>("/stats/overviewvoucher"),

        fetchJson<RankingItem[]>("/stats/rankingday"),

        fetchJson<RankingItem[]>("/stats/rankingweek"),
      ]);

      if (!active) {
        return;
      }

      setData({
        overview:
          overviewResult.status === "fulfilled" ? overviewResult.value : null,

        voucherOverview:
          voucherResult.status === "fulfilled" ? voucherResult.value : null,

        rankingDay:
          rankingDayResult.status === "fulfilled" &&
          Array.isArray(rankingDayResult.value)
            ? rankingDayResult.value
            : [],

        rankingWeek:
          rankingWeekResult.status === "fulfilled" &&
          Array.isArray(rankingWeekResult.value)
            ? rankingWeekResult.value
            : [],
      });

      const failures = [
        overviewResult,
        voucherResult,
        rankingDayResult,
        rankingWeekResult,
      ].filter((result) => result.status === "rejected");

      if (failures.length === 4) {
        notify("Impossible de charger le tableau de bord.", {
          type: "error",
        });
      } else if (failures.length > 0) {
        notify(
          "Certaines données du tableau de bord sont temporairement indisponibles.",
          {
            type: "warning",
          },
        );
      }

      setLoading(false);
    };

    void loadDashboard();

    return () => {
      active = false;
    };
  }, [notify]);

  // ============================================================
  // DERIVED VALUES
  // ============================================================

  const overview = data.overview;

  const playCount = numberValue(overview?.play_count);

  const gameCount = numberValue(overview?.game_count);

  const entityCount = numberValue(overview?.entity_count);

  const locationCount = numberValue(overview?.location_count);

  const bestScore = numberValue(overview?.max_row_score);

  const claimed = getClaimedCount(data.voucherOverview);

  const consumed = getConsumedCount(data.voucherOverview);

  const consumptionRate =
    claimed > 0 ? Math.min(Math.round((consumed / claimed) * 100), 100) : 0;

  const currentRanking =
    rankingTab === "day" ? data.rankingDay : data.rankingWeek;

  const sortedRanking = useMemo(() => {
    return [...currentRanking]
      .sort((a, b) => getRankingScore(b) - getRankingScore(a))
      .slice(0, 5);
  }, [currentRanking]);

  const displayName =
    typeof identity?.pseudo === "string"
      ? identity.pseudo
      : typeof identity?.fullName === "string"
        ? identity.fullName
        : "Joueur";

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 420,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress
          sx={{
            color: "#6C0088",
          }}
        />
      </Box>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },

        bgcolor: "#fafafa",

        minHeight: "100vh",
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="#6C0088">
          {getGreeting()} {displayName} 👋
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Voici un aperçu de ton activité sur GAMEOO.
        </Typography>
      </Box>

      {/* ======================================================
          HERO + LAST ACTIVITY
      ====================================================== */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(280px, 0.8fr) minmax(420px, 1.2fr)",
          },

          gap: 2,
          mb: 2,
        }}
      >
        {/* BEST SCORE */}

        <Card
          sx={{
            borderRadius: 4,
            color: "#fff",

            background: "linear-gradient(135deg, #D844F5 0%, #8E00C5 100%)",

            boxShadow: "0 10px 30px rgba(108,0,136,0.18)",
          }}
        >
          <CardContent
            sx={{
              p: 3,
              "&:last-child": {
                pb: 3,
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <EmojiEventsOutlined />

              <Typography variant="body1" fontWeight={600}>
                Ton meilleur score
              </Typography>
            </Stack>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h2" fontWeight={800}>
                {formatNumber(bestScore)}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  opacity: 0.85,
                }}
              >
                points
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: 3,
                opacity: 0.85,
              }}
            >
              Continue à jouer pour améliorer ton record.
            </Typography>
          </CardContent>
        </Card>

        {/* LAST ACTIVITY */}

        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            height: "100%",
          }}
        >
          <CardContent
            sx={{
              p: 3,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: "rgba(108,0,136,.08)",
                  color: "#6C0088",
                }}
              >
                <HistoryOutlined />
              </Box>

              <Box>
                <Typography fontWeight={700}>Dernière activité</Typography>

                <Typography variant="body2" color="text.secondary">
                  Ta dernière session enregistrée
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="h6"
              sx={{
                mt: 3,
              }}
            >
              {formatLastPlay(overview?.last_play)}
            </Typography>

            {playCount === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                }}
              >
                Joue à ton premier jeu pour commencer à alimenter tes
                statistiques.
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                }}
              >
                Tu as déjà joué {formatNumber(playCount)} partie
                {playCount > 1 ? "s" : ""}.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ======================================================
          STATS
      ====================================================== */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            xl: "repeat(4, 1fr)",
          },

          gap: 2,
          mb: 2,
        }}
      >
        <StatCard
          label="Parties jouées"
          value={formatNumber(playCount)}
          icon={<SportsEsportsOutlined />}
        />

        <StatCard
          label="Jeux différents"
          value={formatNumber(gameCount)}
          icon={<VideogameAssetOutlined />}
        />

        <StatCard
          label="Entités visitées"
          value={formatNumber(entityCount)}
          icon={<BusinessOutlined />}
        />

        <StatCard
          label="Lieux visités"
          value={formatNumber(locationCount)}
          icon={<LocationOnOutlined />}
        />
      </Box>

      {/* ======================================================
          VOUCHERS + RANKING
      ====================================================== */}

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            lg: "1fr 1fr",
          },

          gap: 2,
        }}
      >
        {/* VOUCHERS */}

        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
          }}
        >
          <CardContent
            sx={{
              p: 3,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color="#6C0088">
                  Utilisation des vouchers
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Suivi de tes bons réclamés et consommés
                </Typography>
              </Box>

              <RedeemOutlined
                sx={{
                  color: "#6C0088",
                }}
              />
            </Stack>

            {claimed === 0 && consumed === 0 ? (
              <Box
                sx={{
                  minHeight: 180,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  flexDirection: "column",
                  px: 2,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(108,0,136,.08)",
                    color: "#6C0088",
                    mb: 2,
                  }}
                >
                  <RedeemOutlined />
                </Box>

                <Typography fontWeight={700}>Aucun voucher réclamé</Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    maxWidth: 320,
                  }}
                >
                  Tes futurs vouchers réclamés et consommés apparaîtront ici.
                </Typography>
              </Box>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    mb: 3,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 3,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Réclamés
                    </Typography>

                    <Typography variant="h5" fontWeight={700} color="#6C0088">
                      {formatNumber(claimed)}
                    </Typography>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 3,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Consommés
                    </Typography>

                    <Typography variant="h5" fontWeight={700} color="#6C0088">
                      {formatNumber(consumed)}
                    </Typography>
                  </Paper>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Taux d'utilisation</Typography>

                  <Typography variant="body2" fontWeight={700}>
                    {consumptionRate}%
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={consumptionRate}
                  sx={{
                    height: 8,
                    borderRadius: 10,

                    bgcolor: "rgba(108,0,136,.10)",

                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#6C0088",
                      borderRadius: 10,
                    },
                  }}
                />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 2,
                  }}
                >
                  {consumed} voucher
                  {consumed > 1 ? "s" : ""} consommé
                  {consumed > 1 ? "s" : ""} sur {claimed} réclamé
                  {claimed > 1 ? "s" : ""}.
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* RANKING */}

        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
          }}
        >
          <CardContent
            sx={{
              p: 0,
            }}
          >
            <Box
              sx={{
                px: 3,
                pt: 3,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmojiEventsOutlined
                  sx={{
                    color: "#6C0088",
                  }}
                />

                <Typography variant="h6" fontWeight={700} color="#6C0088">
                  Top des meilleurs joueurs
                </Typography>
              </Stack>
            </Box>

            <Tabs
              value={rankingTab}
              onChange={(_event, value: "day" | "week") => setRankingTab(value)}
              sx={{
                px: 2,
                mt: 1,

                "& .MuiTab-root": {
                  minWidth: 90,
                },

                "& .Mui-selected": {
                  color: "#6C0088 !important",
                },

                "& .MuiTabs-indicator": {
                  bgcolor: "#D221FF",
                },
              }}
            >
              <Tab label="Jour" value="day" />

              <Tab label="Semaine" value="week" />
            </Tabs>

            <Divider />

            <Box
              sx={{
                p: 3,
                minHeight: 250,
              }}
            >
              {sortedRanking.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    textAlign: "center",
                  }}
                >
                  <EmojiEventsOutlined
                    sx={{
                      fontSize: 44,
                      color: "text.disabled",
                    }}
                  />

                  <Typography
                    fontWeight={700}
                    sx={{
                      mt: 1.5,
                    }}
                  >
                    {rankingTab === "day"
                      ? "Aucun classement aujourd’hui"
                      : "Aucun classement cette semaine"}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    Joue pour tenter d'apparaître dans le classement.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {sortedRanking.map((item, index) => {
                    const userId = getRankingUserId(item);

                    const isMe = userId === identity?.id;

                    return (
                      <Box
                        key={userId ?? `${getRankingName(item)}-${index}`}
                        sx={{
                          display: "grid",

                          gridTemplateColumns: "42px 1fr auto",

                          gap: 1.5,

                          alignItems: "center",

                          px: 1.5,
                          py: 1.25,

                          borderRadius: 2,

                          bgcolor: isMe ? "rgba(108,0,136,.07)" : "transparent",
                        }}
                      >
                        <Box>
                          {index < 3 ? (
                            <Typography fontSize={22}>
                              {["🥇", "🥈", "🥉"][index]}
                            </Typography>
                          ) : (
                            <Typography fontWeight={700} color="text.secondary">
                              #{index + 1}
                            </Typography>
                          )}
                        </Box>

                        <Box
                          sx={{
                            minWidth: 0,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography fontWeight={isMe ? 700 : 600} noWrap>
                              {getRankingName(item)}
                            </Typography>

                            {isMe && (
                              <Chip
                                label="Moi"
                                size="small"
                                sx={{
                                  height: 20,
                                }}
                              />
                            )}
                          </Stack>
                        </Box>

                        <Typography fontWeight={700} color="#6C0088">
                          {formatNumber(getRankingScore(item))} pts
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
